import { useCallback, useState } from 'react';

interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

const teamImages: LightboxImage[] = [
  {
    src: 'https://static.readdy.ai/image/86a2daa6c77fa935b9acdedfc02e0e91/df99bc8eaed0add13e2205168b7cea0b.jpeg',
    alt: 'Equipo de Rixin Group en instalaciones petroleras de China con uniformes rojos y cascos blancos de seguridad',
    caption: 'Equipo Técnico Rixin Group — Visita a Campo Petrolero',
  },
];

export default function RixinTeam() {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightbox = useCallback(() => setLightboxOpen(true), []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  return (
    <section className="bg-background-100 py-16 md:py-24 border-t border-background-200/60">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <div
                onClick={openLightbox}
                className="group relative rounded-lg overflow-hidden border border-background-200/60 cursor-pointer select-none"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openLightbox(); }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={teamImages[0].src}
                    alt={teamImages[0].alt}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 select-none"
                    draggable="false"
                  />
                </div>
                {/* Anti-download overlay */}
                <div
                  className="absolute inset-0 z-10"
                  onContextMenu={(e) => e.preventDefault()}
                  aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 md:p-5">
                  <p className="text-white font-heading text-sm md:text-base font-semibold">{teamImages[0].caption}</p>
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                  <i className="ri-zoom-in-line text-sm md:text-base" />
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-5">
                <i className="ri-team-line text-sm w-4 h-4 flex items-center justify-center" />
                Nuestro Equipo en China
              </div>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                Profesionales Comprometidos <span className="text-primary-500">con la Excelencia</span>
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5 text-left sm:text-justify">
                El equipo técnico de <strong className="text-foreground-950">Rixin Group</strong> trabaja directamente en los campos petroleros y plantas de producción de China, asegurando que cada lote de demulsificante cumpla con los más altos estándares de calidad antes de ser exportado.
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6 text-left sm:text-justify">
                Con uniformes de seguridad, cascos certificados y credenciales de acceso, nuestros especialistas supervisan personalmente la formulación, empaque y carga de cada envío destinado a Venezuela.
              </p>

              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="bg-background-50 rounded-lg p-3 md:p-4 border border-background-200/60">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-2">
                    <i className="ri-shield-check-line text-base md:text-lg" />
                  </div>
                  <h4 className="font-heading text-sm font-bold text-foreground-950 mb-1">Seguridad Industrial</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">Protocolos estrictos en planta y campo</p>
                </div>
                <div className="bg-background-50 rounded-lg p-3 md:p-4 border border-background-200/60">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-2">
                    <i className="ri-user-star-line text-base md:text-lg" />
                  </div>
                  <h4 className="font-heading text-sm font-bold text-foreground-950 mb-1">Talento Especializado</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">Ingenieros químicos con experiencia petrolera</p>
                </div>
                <div className="bg-background-50 rounded-lg p-3 md:p-4 border border-background-200/60">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-2">
                    <i className="ri-flask-line text-base md:text-lg" />
                  </div>
                  <h4 className="font-heading text-sm font-bold text-foreground-950 mb-1">Control de Calidad</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">Pruebas en línea antes de cada embarque</p>
                </div>
                <div className="bg-background-50 rounded-lg p-3 md:p-4 border border-background-200/60">
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-2">
                    <i className="ri-global-line text-base md:text-lg" />
                  </div>
                  <h4 className="font-heading text-sm font-bold text-foreground-950 mb-1">Operaciones Globales</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">Exportación directa China → Venezuela</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-foreground-950/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-3 right-3 md:top-5 md:right-5 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50 hover:bg-background-50/30 transition-all duration-200 cursor-pointer z-20"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-xl" />
          </button>

          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="rounded-lg overflow-hidden relative">
              <img
                src={teamImages[0].src}
                alt={teamImages[0].alt}
                className="w-full max-h-[65vh] md:max-h-[75vh] object-contain select-none"
                draggable="false"
              />
              {/* Anti-download overlay on lightbox image */}
              <div
                className="absolute inset-0 z-10"
                onContextMenu={(e) => e.preventDefault()}
                aria-hidden="true"
              />
            </div>
            <div className="text-center mt-4">
              <p className="text-white font-heading text-base font-semibold">{teamImages[0].caption}</p>
              <p className="text-foreground-400 text-xs mt-1">{teamImages[0].alt}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}